import { useEffect, useState } from "react";
import StatCard from "pages/dashboard-overview/components/StatCard";
import StatCardSkeleton from "pages/analytics/components/StatCardSkeleton";
import { ErrorBanner } from "pages/analytics/components/AnalyticsStates";
import { formatCurrency } from "pages/analytics/components/analyticsFormatters";
import { getAttributionBreakdown } from "services/marketing-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";
import {
  formatAccountLabel,
  formatEntryPointLabel,
} from "../utils/attributionDashboardUtils";
const topByRevenue = (rows) => rows?.[0] ?? null;

export default function AttributionTopKpiCards({ api, filters }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getAttributionBreakdown(api, { filters });
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) {
          setData(null);
          setError("Failed to load top attribution KPIs.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  if (isCustomRangeIncomplete(filters) && !isLoading) {
    return (
      <p className="text-sm text-gray-500 italic mb-6">
        Select start and end dates to view top attribution KPIs.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6">
        <ErrorBanner message={error} />
      </div>
    );
  }

  const topPlatform = topByRevenue(data?.byPlatform);
  const topAccount = topByRevenue(data?.byAccount);
  const topEntryPoint = topByRevenue(data?.byEntryPoint);

  const platformName = topPlatform?.name ?? "-";
  const accountName = topAccount ? formatAccountLabel(topAccount.name) : "-";
  const entryPointName = topEntryPoint ? formatEntryPointLabel(topEntryPoint.name) : "-";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <StatCard
        title="Top Platform"
        value={platformName}
        suffix={topPlatform ? ` · ${formatCurrency(topPlatform.attributedRevenue)}` : ""}
        icon="Globe"
      />
      <StatCard
        title="Top Account"
        value={accountName}
        suffix={topAccount ? ` · ${formatCurrency(topAccount.attributedRevenue)}` : ""}
        icon="User"
      />
      <StatCard
        title="Top Entry Point"
        value={entryPointName}
        suffix={topEntryPoint ? ` · ${formatCurrency(topEntryPoint.attributedRevenue)}` : ""}
        icon="Link"
      />
    </div>
  );
}
